<script setup>
import { computed } from "vue";
import { getFileTypeLabel, getFileTypeBadgeClass } from "../../composables/useFileType.js";

// Same fields as DocumentDetailsPanel.vue (the standalone "View Details"
// slide-over opened from a document's kebab menu) — this is the same content,
// just embedded as a tab inside the document viewer's side panel instead of
// its own floating panel, so the two intentionally share field logic rather
// than one importing the other's fetch-driven composable.
const props = defineProps({
  document: { type: Object, default: () => ({}) },
  userRole: { type: String, default: null },
});

const fileTypeLabel = computed(() => getFileTypeLabel(props.document?.origin?.mimeType ?? props.document?.originMimeType, props.document?.name));
const fileTypeBadgeClass = computed(() => getFileTypeBadgeClass(props.document?.origin?.mimeType ?? props.document?.originMimeType));

const SOURCE_LABELS = {
  ONE_DRIVE: "OneDrive",
  GOOGLE_DRIVE: "Google Drive",
  BOX: "Box",
  DROPBOX: "Dropbox",
  LOCAL: "Local file",
};
const canViewSource = computed(() => props.userRole !== "VIEWER");
const sourceLabel = computed(() => SOURCE_LABELS[props.document?.source] ?? props.document?.source);
const showSourceLink = computed(() => canViewSource.value && props.document?.source && props.document.source !== "LOCAL" && props.document?.webViewLink);

function formatDetailDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const datePart = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const timePart = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${datePart}, at ${timePart}`;
}

function formatFileSize(bytes) {
  if (!bytes) return "—";
  if (bytes <= 1_000_000) return `${(bytes / 1000).toFixed(2)} KB`;
  return `${(bytes / 1_000_000).toFixed(2)} MB`;
}
</script>

<template>
  <div class="p-4">
    <div class="relative h-36 rounded-xl bg-slate-100 dark:bg-slate-900 flex items-center justify-center overflow-hidden mb-3">
      <img v-if="document?.preview" :src="document.preview" class="h-28 w-auto max-w-[75%] object-contain shadow" />
      <span class="absolute bottom-0 left-0 px-2.5 py-1 rounded-tr-lg text-[10px] font-bold text-white uppercase" :class="fileTypeBadgeClass">{{ fileTypeLabel }}</span>
    </div>

    <h3 class="text-sm font-bold text-slate-800 dark:text-slate-100 m-0 mb-4 break-words">{{ document?.name ?? "Untitled" }}</h3>

    <div class="flex flex-col gap-3.5">
      <div v-if="document?.uploaderName">
        <p class="text-[12px] text-slate-400 dark:text-slate-500 m-0 mb-0.5">Uploaded by :</p>
        <p class="text-[13px] font-medium text-slate-800 dark:text-slate-100 m-0">{{ document.uploaderName }}</p>
      </div>

      <div>
        <p class="text-[12px] text-slate-400 dark:text-slate-500 m-0 mb-0.5">Date uploaded :</p>
        <p class="text-[13px] font-medium text-slate-800 dark:text-slate-100 m-0">{{ formatDetailDate(document?.createdAt) }}</p>
      </div>

      <div>
        <p class="text-[12px] text-slate-400 dark:text-slate-500 m-0 mb-0.5">Last modified :</p>
        <p class="text-[13px] font-medium text-slate-800 dark:text-slate-100 m-0">{{ formatDetailDate(document?.updatedAt) }}</p>
      </div>

      <div>
        <p class="text-[12px] text-slate-400 dark:text-slate-500 m-0 mb-0.5">File type :</p>
        <p class="text-[13px] font-medium text-slate-800 dark:text-slate-100 m-0">{{ fileTypeLabel }}</p>
      </div>

      <div>
        <p class="text-[12px] text-slate-400 dark:text-slate-500 m-0 mb-0.5">Size :</p>
        <p class="text-[13px] font-medium text-slate-800 dark:text-slate-100 m-0">{{ formatFileSize(document?.sourceSize) }}</p>
      </div>

      <div v-if="canViewSource && document?.source">
        <p class="text-[12px] text-slate-400 dark:text-slate-500 m-0 mb-0.5">Source :</p>
        <a
          v-if="showSourceLink"
          :href="document.webViewLink"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-[13px] font-medium text-blue-500 dark:text-blue-400 hover:underline"
        >
          {{ sourceLabel }}
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
            <path d="M7 17L17 7M7 7h10v10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
        <p v-else class="text-[13px] font-medium text-slate-800 dark:text-slate-100 m-0">{{ sourceLabel }}</p>
      </div>
    </div>
  </div>
</template>
